import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Calendar, Loader2, Users, MousePointerClick, ChefHat, ShoppingCart, CalendarCheck, User, RefreshCw, ArrowRight, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_ICONS = {
  portal_login: Users,
  nutrition_page_viewed: ChefHat,
  nutrition_entry_created: ChefHat,
  ai_meal_plan_generated: TrendingUp,
  ai_weekly_plan_generated: Calendar,
  ai_shopping_list_generated: ShoppingCart,
  booking_created: CalendarCheck,
  profile_viewed: User,
};

const EVENT_COLORS = {
  portal_login: 'text-blue-400 bg-blue-500/15 border-blue-500/30',
  nutrition_page_viewed: 'text-emerald-600 bg-emerald-500/15 border-emerald-500/30',
  nutrition_entry_created: 'text-emerald-600 bg-emerald-500/15 border-emerald-500/30',
  ai_meal_plan_generated: 'text-purple-400 bg-purple-500/15 border-purple-500/30',
  ai_weekly_plan_generated: 'text-purple-400 bg-purple-500/15 border-purple-500/30',
  ai_shopping_list_generated: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
  booking_created: 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30',
  profile_viewed: 'text-gray-500 bg-zinc-500/15 border-zinc-500/30',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export default function FunnelTrackingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    const start = new Date(now.getTime() - 30 * 86400000).toISOString().split('T')[0];
    setStartDate(start);
    setEndDate(end);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/funnel-tracking/summary`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Funnel-Daten');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Event order for display
  const eventOrder = ['portal_login', 'nutrition_entry_created', 'ai_meal_plan_generated', 'ai_weekly_plan_generated', 'ai_shopping_list_generated', 'booking_created', 'profile_viewed', 'nutrition_page_viewed'];

  // Build sorted event list
  const sortedEvents = eventOrder
    .filter(e => data?.event_counts?.[e])
    .map(e => ({ type: e, ...data.event_counts[e] }));

  // Also add any events not in the predefined order
  if (data?.event_counts) {
    Object.entries(data.event_counts).forEach(([type, info]) => {
      if (!eventOrder.includes(type)) sortedEvents.push({ type, ...info });
    });
  }

  const totalLogin = data?.event_counts?.portal_login?.count || 0;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="funnel-tracking-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funnel-Tracking</h1>
          <p className="text-sm text-gray-400 mt-0.5">Portal-Interaktionen deiner Kunden</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="bg-gray-100 border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            data-testid="funnel-start-date"
          />
          <span className="text-gray-400">–</span>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="bg-gray-100 border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            data-testid="funnel-end-date"
          />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="border-gray-300 text-gray-600" data-testid="funnel-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-gray-400 text-center py-10">Keine Daten verfügbar</p>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="funnel-summary-cards">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                  <MousePointerClick className="w-4 h-4 text-blue-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{data.total_events}</p>
              <p className="text-xs text-gray-400">Gesamt-Events</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Users className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{data.unique_customers}</p>
              <p className="text-xs text-gray-400">Aktive Kunden</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-purple-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {data.unique_customers > 0 ? (data.total_events / data.unique_customers).toFixed(1) : '0'}
              </p>
              <p className="text-xs text-gray-400">Events/Kunde</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-amber-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {data.daily_breakdown ? Object.keys(data.daily_breakdown).length : 0}
              </p>
              <p className="text-xs text-gray-400">Aktive Tage</p>
            </div>
          </div>

          {/* Event Funnel Cards */}
          <div className="space-y-2" data-testid="funnel-event-cards">
            <h2 className="text-base font-semibold text-gray-700">Events nach Typ</h2>
            {sortedEvents.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">Keine Events im gewählten Zeitraum</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedEvents.map(({ type, count, label }) => {
                  const Icon = EVENT_ICONS[type] || MousePointerClick;
                  const colorCls = EVENT_COLORS[type] || 'text-gray-500 bg-zinc-500/15 border-zinc-500/30';
                  const [textColor, bgColor] = colorCls.split(' ');
                  const pct = totalLogin > 0 ? Math.round((count / totalLogin) * 100) : 0;
                  return (
                    <div key={type} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4" data-testid={`funnel-event-${type}`}>
                      <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${textColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500 truncate">{label}</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-xl font-bold text-gray-900">{count}</p>
                          {type !== 'portal_login' && totalLogin > 0 && (
                            <span className="text-xs text-gray-400">{pct}% der Logins</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Funnel Visualization */}
          {sortedEvents.length > 1 && totalLogin > 0 && (
            <div className="space-y-2" data-testid="funnel-visualization">
              <h2 className="text-base font-semibold text-gray-700">Trichter</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
                {sortedEvents.map(({ type, count, label }, idx) => {
                  const widthPct = Math.max(8, (count / totalLogin) * 100);
                  const colorCls = EVENT_COLORS[type] || 'text-gray-500 bg-zinc-500/15 border-zinc-500/30';
                  const bgClass = colorCls.split(' ')[1] || 'bg-zinc-500/15';
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-28 text-right truncate">{label}</span>
                      <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                        <div
                          className={`h-full ${bgClass} rounded-lg transition-all duration-500 flex items-center justify-end pr-2`}
                          style={{ width: `${widthPct}%` }}
                        >
                          <span className="text-xs font-medium text-gray-900">{count}</span>
                        </div>
                      </div>
                      {idx < sortedEvents.length - 1 && (
                        <span className="text-xs text-gray-400 w-10 text-right">
                          {totalLogin > 0 ? Math.round((count / totalLogin) * 100) : 0}%
                        </span>
                      )}
                      {idx === 0 && <span className="text-xs text-gray-400 w-10 text-right">100%</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Customers */}
            {data.top_customers?.length > 0 && (
              <div className="space-y-2" data-testid="funnel-top-customers">
                <h2 className="text-base font-semibold text-gray-700">Aktivste Kunden</h2>
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {data.top_customers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono text-gray-400 w-5">{i + 1}.</span>
                          <span className="text-sm text-gray-900">{c.name}</span>
                        </div>
                        <span className="text-sm font-medium text-emerald-600">{c.events} Events</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Events */}
            {data.recent_events?.length > 0 && (
              <div className="space-y-2" data-testid="funnel-recent-events">
                <h2 className="text-base font-semibold text-gray-700">Letzte Aktivitäten</h2>
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                  <div className="divide-y divide-gray-200">
                    {data.recent_events.map((ev, i) => {
                      const Icon = EVENT_ICONS[ev.event_type] || MousePointerClick;
                      const colorCls = EVENT_COLORS[ev.event_type] || 'text-gray-500';
                      const textColor = colorCls.split(' ')[0];
                      return (
                        <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                          <Icon className={`w-4 h-4 ${textColor} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{data.event_labels?.[ev.event_type] || ev.event_type}</p>
                            <p className="text-xs text-gray-400">{ev.customer_name || 'Unbekannt'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-500">{formatDate(ev.timestamp)}</p>
                            <p className="text-xs text-gray-400">{formatTime(ev.timestamp)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
